/* Collection order is computed from ALL current filtered Shopify pages, never a saved catalog.
   Network collection is serial; no cart mutations, animation loop or eager image preloading. */
(function (global) {
  'use strict';
  const compare = (a, b) => a.key < b.key ? -1 : a.key > b.key ? 1 : 0;
  function validatePages(pages, expectedPages, expectedTotal, keepServerOrder = false) {
    if (pages.length !== expectedPages) throw Error('Incomplete collection pages');
    const numbers = new Set(), ids = new Set(), rows = [];
    for (const page of [...pages].sort((a,b)=>a.page-b.page)) {
      if (page.total !== expectedTotal || page.pages !== expectedPages || numbers.has(page.page)) throw Error('Collection changed while loading');
      numbers.add(page.page);
      for (const row of page.rows) {
        if (!row.id || ids.has(row.id)) throw Error('Duplicate or missing product identity');
        ids.add(row.id); rows.push(row);
      }
    }
    for (let number = 1; number <= expectedPages; number++) if (!numbers.has(number)) throw Error('Missing page');
    if (ids.size !== expectedTotal) throw Error('Collection total changed while loading');
    const included = rows.filter(row => row.included);
    return keepServerOrder ? included : included.sort(compare);
  }
  function pageURL(href, number, param, section) {
    const url = new URL(href);
    url.searchParams.delete('r24_page');
    url.searchParams.set(param || 'page', String(number));
    url.searchParams.set('section_id', section);
    return url;
  }
  function displayWindow(total, requested, desktop) {
    const size=desktop?60:24,pages=Math.max(1,Math.ceil(total/size));
    const page=Math.min(pages,Math.max(1,Number.isFinite(requested)?Math.floor(requested):1));
    return {desktop,pages,page,start:desktop?(page-1)*size:0,end:Math.min(page*size,total)};
  }
  const api = { compare, validatePages, pageURL, displayWindow };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (!global.customElements || global.customElements.get('r24-collection-order')) return;
  class CollectionOrder extends HTMLElement {
    connectedCallback() {
      this.abort?.abort(); this.abort = new AbortController();
      this.grid = this.querySelector('[data-order-grid]');
      this.status = this.querySelector('[data-order-status]');
      this.pager = this.querySelector('[data-order-pagination]');
      this.nativePager = this.querySelector('[data-native-pagination]');
      this.initial = this.snapshot(this);
      this.desktopPages=matchMedia('(min-width:1024px)');
      this.desktopPages.addEventListener('change',()=>{if(this.rows)this.navigatePage(1,false,false);},{signal:this.abort.signal});
      global.addEventListener('popstate',()=>{if(this.rows)this.show(Number(new URL(location.href).searchParams.get('r24_page'))||1,false);},{signal:this.abort.signal});
      const form = this.querySelector('form');
      form?.addEventListener('change', event => {
        if (!event.target.matches('[data-sort]')) return;
        event.stopImmediatePropagation(); form.requestSubmit();
      }, { capture: true, signal: this.abort.signal });
      form?.addEventListener('submit', event => {
        event.preventDefault();
        const url = new URL(location.href);
        [...url.searchParams.keys()].forEach(key => {
          if (key.startsWith('filter.') || ['sort_by', 'page', 'r24_page', this.dataset.pageParam].includes(key)) url.searchParams.delete(key);
        });
        for (const [key, value] of new FormData(form)) if (value !== '' && key !== 'view') url.searchParams.append(key, value);
        if (new FormData(form).get('view')) url.searchParams.set('view', new FormData(form).get('view'));
        // Retain current view/preview/locale and any collection search context.
        location.assign(url.href);
      }, { signal: this.abort.signal });
      document.addEventListener('venlu:languagechange', () => this.localize(), { signal: this.abort.signal });
      this.querySelector('[data-order-filters]')?.addEventListener('change', () => {
        if (!this.allRows) return;
        const url = new URL(location.href);
        for (const [name, selector] of [['r24_kind', '[data-order-kind]'], ['r24_audience', '[data-order-audience]']]) {
          const value = this.querySelector(selector)?.value || 'all';
          if (value === 'all') url.searchParams.delete(name); else url.searchParams.set(name, value);
        }
        url.searchParams.delete('r24_page'); history.replaceState(history.state, '', url);
        this.filterRows(); this.show(1, false);
      }, { signal: this.abort.signal });
      // An explicit user sort is Shopify's order, not a local per-page regrouping.
      this.nativeSort = Boolean(new URL(location.href).searchParams.get('sort_by'));
      this.load();
    }
    disconnectedCallback() { this.abort?.abort(); this.request?.abort(); }
    text(de, en) { return document.body.dataset.locale === 'de' ? de : en; }
    snapshot(root) {
      return { page: Math.max(1, Number(root.dataset.page)), pages: Math.max(1, Number(root.dataset.pages)), total: Number(root.dataset.total), rows: [...root.querySelectorAll('[data-catalog-record]')].map(node => ({ id: node.dataset.productId, key: node.dataset.orderKey, included: node.dataset.included === 'true', node })) };
    }
    localize() {
      if (this.loading) this.status.textContent = this.nativeSort ? this.text("Pieces werden geladen …", "Loading pieces …") : window.VenluI18n.t("r24.s_ffafd39fa081",{},"Loading Prime and character order …");
      else if (this.rows) this.describe();
    }
    async load() {
      if (this.loading) return;
      this.loading = true; this.status.hidden = false; this.localize();
      this.setAttribute('aria-busy', 'true');
      const pages = [this.initial], expectedPages = this.initial.pages || 1;
      try {
        for (let number = 1; number <= expectedPages; number++) {
          if (number === this.initial.page) continue;
          this.request = new AbortController();
          const abortRequest = () => this.request?.abort();
          this.abort.signal.addEventListener('abort', abortRequest, { once: true });
          const timer = setTimeout(abortRequest, 15000);
          try {
            const response = await fetch(pageURL(location.href, number, this.dataset.pageParam, this.dataset.section), { credentials: 'same-origin', signal: this.request.signal, headers: { Accept: 'text/html' } });
            if (!response.ok) throw Error('Collection request failed');
            // A template keeps fetched product images and scripts inert until a visible card is adopted.
            const parsed = document.createElement('template'); parsed.innerHTML = await response.text();
            const root = [...parsed.content.querySelectorAll('r24-collection-order')].find(node => node.dataset.section === this.dataset.section && node.dataset.collection === this.dataset.collection);
            if (!root || Number(root.dataset.page) !== number) throw Error('Wrong collection response');
            pages.push(this.snapshot(root));
          } finally { clearTimeout(timer); this.abort.signal.removeEventListener('abort', abortRequest); }
        }
        if (!this.isConnected) return;
        this.allRows = validatePages(pages, expectedPages, this.initial.total, this.nativeSort);
        const filters = this.querySelector('[data-order-filters]');
        if (filters) {
          filters.hidden = false;
          const url = new URL(location.href);
          for (const [name, selector] of [['r24_kind', '[data-order-kind]'], ['r24_audience', '[data-order-audience]']]) {
            const select = filters.querySelector(selector), value = url.searchParams.get(name);
            if ([...select.options].some(option => option.value === value)) select.value = value;
          }
        }
        this.filterRows();
        this.loading = false;
        this.nativePager.hidden = true;
        const requestedPage = Number(new URL(location.href).searchParams.get('r24_page')) || 1;
        this.show(requestedPage, false);
        this.dataset.orderReady = 'true';
      } catch (error) {
        if (!this.isConnected || this.abort.signal.aborted) return;
        this.dataset.orderError = 'true';
        this.status.textContent = window.VenluI18n.t("r24.s_b95115bfcb5a",{},"The full order could not load. All pieces remain accessible through the product pages.");
        const retry = document.createElement('button'); retry.type = 'button';
        retry.textContent = window.VenluI18n.t("r24.s_d8b8392e2c54",{},"Try again");
        retry.addEventListener('click', () => this.load(), { once: true }); this.status.append(retry);
      } finally { this.loading = false; this.removeAttribute('aria-busy'); }
    }
    describe() {
      const start = this.rows.length ? this.start + 1 : 0;
      const end = this.visibleCount;
      if(this.nativeSort){this.status.textContent = this.text(`${start}–${end} von ${this.rows.length} Pieces`,`${start}–${end} of ${this.rows.length} pieces`);return;}
      this.status.textContent = window.VenluI18n.t("r24.s_72b2b8d514af",{p1:start,p2:end,p3:this.rows.length},"{{ p1 }}–{{ p2 }} of {{ p3 }} pieces").split(" · ")[0];
    }
    filterRows() {
      const kind = this.querySelector('[data-order-kind]')?.value || 'all';
      const audience = this.querySelector('[data-order-audience]')?.value || 'all';
      this.rows = this.allRows.filter(row => (kind === 'all' || kind.split(',').includes(row.node.dataset.kind)) && (audience === 'all' || row.node.dataset.audience === audience));
      const count = this.querySelector('[data-order-count]'); if (count) count.textContent = this.rows.length;
    }
    navigatePage(page, focus, push = true) {
      const url = new URL(location.href);
      url.searchParams.delete(this.dataset.pageParam || 'page');
      url.searchParams.set('r24_page', String(page));
      if(push) history.pushState(history.state, '', url);
      else history.replaceState(history.state, '', url);
      this.show(page, focus);
    }
    show(number, focus) {
      const previous = this.visibleCount || 0;
      const state = displayWindow(this.rows.length, number, this.desktopPages.matches);
      this.current = state.page; this.start = state.start; this.visibleCount = state.end;
      if(focus && !state.desktop) this.grid.append(...this.rows.slice(previous, state.end).map(row=>row.node));
      else this.grid.replaceChildren(...this.rows.slice(state.start, state.end).map(row=>row.node));
      this.querySelector('[data-order-empty]').hidden = this.rows.length > 0;
      this.pager.replaceChildren();
      this.pager.hidden = state.desktop ? state.pages <= 1 : state.end >= this.rows.length;
      if(!this.pager.hidden && state.desktop){
        const shown = new Set([1, state.pages, state.page-1, state.page, state.page+1]);
        let previousPage = 0;
        for(const page of [...shown].filter(n=>n>0&&n<=state.pages).sort((a,b)=>a-b)){
          if(previousPage && page-previousPage>1){const gap=document.createElement('span');gap.textContent='…';gap.setAttribute('aria-hidden','true');this.pager.append(gap);}
          const link=document.createElement('a'),url=new URL(location.href);
          url.searchParams.delete(this.dataset.pageParam||'page');url.searchParams.set('r24_page',String(page));
          link.href=url.href;link.textContent=String(page);
          link.setAttribute('aria-label',this.text(`Produktseite ${page}`,`Product page ${page}`));
          if(page===state.page)link.setAttribute('aria-current','page');
          link.addEventListener('click',event=>{
            if(event.button!==0||event.ctrlKey||event.metaKey||event.shiftKey||event.altKey)return;
            event.preventDefault();this.navigatePage(page,true);
          });
          this.pager.append(link);previousPage=page;
        }
      }else if(!this.pager.hidden){
        const button=document.createElement('button');button.type='button';button.className='r19-btn r19-btn--line r36-catalog-more';
        const added=Math.min(state.end,this.rows.length-state.end);
        button.textContent=this.text('Weitere Pieces laden','Load more pieces')+' · +'+added;
        button.addEventListener('click',()=>this.navigatePage(Math.ceil(state.end*2/24),true,false));this.pager.append(button);
      }
      this.localize();
      if(focus && state.desktop){this.grid.focus({preventScroll:true});this.grid.scrollIntoView({block:'start',behavior:'instant'});}
      else if(focus){this.rows[previous]?.node.querySelector('a')?.focus({preventScroll:true});}
    }
  }
  global.customElements.define('r24-collection-order', CollectionOrder);
})(typeof window !== 'undefined' ? window : globalThis);
